import { useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { AnimatePresence, motion, type Transition } from 'framer-motion'
import { MessageCircle } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { buttonVariants, Tooltip } from '@highlight/ui'
import { StreamingChatIndicator, STREAMING_CHAT_INDICATOR_BLUE } from '@renderer/components/streaming-chat-indicator'
import { FLOATING_FILL_CLASS } from '@renderer/components/floating-surface'

/** The utility cluster's chat circle. The host owns which drawer it opens. */
export interface ActionViewChatControl {
  open: boolean
  onOpen: () => void
  /** A turn is in flight in that chat, so the circle widens into a status pill. */
  applyingChanges?: boolean
}

/**
 * Collapsed and expanded widths, in px, on Button's md box and glyph so the
 * collapsed state matches the footer's other icon buttons. The glyph's inset is
 * half the spare room, so the pill grows rightwards and the glyph stays put.
 */
const CIRCLE_SIZE = 28
const GLYPH_SIZE = 14
const GLYPH_INSET = (CIRCLE_SIZE - GLYPH_SIZE) / 2
const LABEL_GAP = 6
const TRAILING_INSET = 10

/** One curve for the width, the label, and the glyph swap. */
const chatControlTransition: Transition = { duration: 0.24, ease: [0.32, 0.72, 0, 1] }

/**
 * The label's rendered width. `width: 'auto'` gives framer nothing to
 * interpolate, so the button animates to a number, and the string is translated,
 * so that number has to come from the DOM.
 */
function useMeasuredWidth() {
  const ref = useRef<HTMLSpanElement>(null)
  const [width, setWidth] = useState(0)

  useLayoutEffect(() => {
    const element = ref.current
    if (!element) return
    // scrollWidth, not offsetWidth: the collapsed button clips the label.
    const measure = () => setWidth(element.scrollWidth)
    measure()
    // Fonts land after first paint, and the string changes with the locale.
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [])

  return [ref, width] as const
}

/**
 * The footer's chat circle, which widens into "Applying changes…" while the
 * thread is working. One button throughout rather than a swap to a separate
 * pill, so the status stays pressable.
 */
export function ActionViewChatCircle({ open, onOpen, applyingChanges = false }: ActionViewChatControl) {
  const [labelRef, labelWidth] = useMeasuredWidth()
  const expandedWidth = GLYPH_INSET + GLYPH_SIZE + LABEL_GAP + labelWidth + TRAILING_INSET

  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <motion.button
            type="button"
            // The label sits inside the button and would otherwise name it
            // "Applying changes…", losing what pressing it does.
            aria-label={applyingChanges ? 'Open chat, applying changes' : 'Open chat'}
            aria-pressed={open}
            onClick={onOpen}
            // `initial={false}` so a bar that mounts mid-turn opens already wide.
            initial={false}
            animate={{ width: applyingChanges ? expandedWidth : CIRCLE_SIZE }}
            transition={chatControlTransition}
            // A `motion.button` for the width, dressed as the Button it sits beside.
            className={twMerge(
              buttonVariants({ variant: 'outline', shape: 'square' }),
              FLOATING_FILL_CLASS,
              'justify-start overflow-hidden',
            )}
            style={{ paddingLeft: GLYPH_INSET }}
          >
            <span className="relative flex size-3.5 shrink-0 items-center justify-center">
              {/* Stacked rather than swapped in flow, so the crossfade can't nudge
              the label mid-animation. */}
              <motion.span
                className="absolute inset-0 flex items-center justify-center"
                initial={false}
                animate={{ opacity: applyingChanges ? 0 : 1 }}
                transition={chatControlTransition}
              >
                <MessageCircle aria-hidden="true" className="size-3.5" />
              </motion.span>
              <AnimatePresence initial={false}>
                {applyingChanges && (
                  <motion.span
                    key="loader"
                    className="absolute inset-0 flex items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={chatControlTransition}
                  >
                    <StreamingChatIndicator label="Applying changes" size={GLYPH_SIZE} />
                  </motion.span>
                )}
              </AnimatePresence>
            </span>
            {/* Always mounted so its width stays measurable; the collapsed button
            clips it. */}
            <motion.span
              ref={labelRef}
              aria-hidden="true"
              initial={false}
              animate={{ opacity: applyingChanges ? 1 : 0 }}
              transition={chatControlTransition}
              // Matches the loader's blue instead of taking the foreground tint.
              className="type-ui-sm shrink-0 whitespace-nowrap font-medium"
              style={{ marginLeft: LABEL_GAP, color: STREAMING_CHAT_INDICATOR_BLUE }}
            >
              Applying changes…
            </motion.span>
          </motion.button>
        }
      />
      <Tooltip.Content>Open chat</Tooltip.Content>
    </Tooltip>
  )
}

/**
 * The controls the widening pill displaces. `mode="popLayout"` pulls them out of
 * the row's flow on exit so the pill takes their width as they go.
 */
export function ActionViewChatCircleNeighbours({ hidden, children }: { hidden: boolean; children: ReactNode }) {
  return (
    <AnimatePresence initial={false} mode="popLayout">
      {!hidden && (
        <motion.div
          key="neighbours"
          initial={{ opacity: 0, filter: 'blur(3px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          exit={{ opacity: 0, filter: 'blur(3px)' }}
          transition={chatControlTransition}
          className="flex shrink-0 items-center gap-2"
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
